import React, { FC, useCallback } from 'react';
import * as actions from '../mfaTokensActions';
import TokenTextField from './TokenTextField';
import { Field } from '../types';
import { FIDO2_FIELDS } from '../../../common/fields';
import TokenActions from './TokenActions';
import { TokenComponentProps } from './index';
import Fido2ModeChangedPopup from './Fido2ModeChangedPopup';
import UserIdentifierDropdown from './UserIdentifierDropdown';

const fido2ModeOptions = [
    { title: 'Cloud-based FIDO2', value: 'cloud' },
    { title: 'On-prem FIDO2', value: 'on-prem' }
];

type DispatchFn = (action: UpdateAction) => void;

interface UpdateAction {
    type: typeof actions.UPDATE_FIDO2_MFA_TOKEN;
    payload: {
        fieldId: string;
        value: string;
    };
}

export interface Fido2Fields {
    fido2Mode: Field;
    fido2RpId: Field;
    fido2Origin: Field;
}

export type Fido2Props = TokenComponentProps<Fido2Fields, DispatchFn>;

const PLACEHOLDERS = {
    cloud: {
        origin: 'https://fido2.silverfort.com',
        rpId: 'fido2.silverfort.com'
    },
    onPrem: {
        origin: 'https://silverfort-fido2.{your-domain}',
        rpId: 'silverfort-fido2.{your-domain}'
    }
};

const Fido2: FC<Fido2Props> = ({ editToken, fields, dispatch, onSave, onCancel }) => {
    const [popupOpen, setPopupOpen] = React.useState(false);
    const fido2Mode = fields[FIDO2_FIELDS.FIDO2_MODE.ID];
    const fido2RpId = fields[FIDO2_FIELDS.FIDO2_RP_ID.ID];
    const fido2Origin = fields[FIDO2_FIELDS.FIDO2_ORIGIN.ID];

    const selectedMode = fido2Mode.value === 'on-prem' ? 'onPrem' : 'cloud';

    const isSaveDisabled = [fido2RpId, fido2Origin].some((field) => !field.value);

    const dispatchUpdate = useCallback(
        (fieldId: string, value: string) => {
            dispatch({
                type: actions.UPDATE_FIDO2_MFA_TOKEN,
                payload: { fieldId, value }
            });
        },
        [dispatch]
    );

    const handleFido2ModeChange = (newMode: string) => {
        if (editToken === 'fido2MfaToken' && fido2Mode.value !== newMode) {
            setPopupOpen(true);
        } else {
            dispatchUpdate(fido2Mode.id, newMode);
        }
    };

    const handleConfirmModeChange = (newMode: string) => {
        dispatchUpdate(fido2Mode.id, newMode);
        dispatchUpdate(fido2RpId.id, '');
        dispatchUpdate(fido2Origin.id, '');
        setPopupOpen(false);
    };
    const closePopup = () => {
        setPopupOpen(false);
    };

    const fieldsConfig = [
        {
            field: fido2Origin,
            placeholder: PLACEHOLDERS[selectedMode].origin
        },
        {
            field: fido2RpId,
            placeholder: PLACEHOLDERS[selectedMode].rpId
        }
    ];

    return (
        <>
            <UserIdentifierDropdown
                options={fido2ModeOptions}
                selectedOption={fido2Mode.value || fido2ModeOptions[0].value}
                onChange={handleFido2ModeChange}
                mandatory
                label={fido2Mode.name}
            />

            {fieldsConfig.map(({ field, placeholder }) => (
                <TokenTextField
                    key={field.id}
                    label={field.name}
                    value={field.value}
                    isSecret={false}
                    placeholder={placeholder}
                    onChange={(val) => dispatchUpdate(field.id, val)}
                />
            ))}

            <div className="flex flex-col justify-end flex-1">
                <TokenActions onSave={onSave} onCancel={onCancel} saveDisabled={isSaveDisabled} />
            </div>
            <Fido2ModeChangedPopup
                open={popupOpen}
                handleConfirm={() => {
                    handleConfirmModeChange(fido2Mode.value === 'on-prem' ? 'cloud' : 'on-prem');
                }}
                handleClose={closePopup}
            />
        </>
    );
};

export default Fido2;
